import { useRef, useState } from "react";

import {
  Breadcrumb,
  Button,
  Col,
  Divider,
  Form,
  Layout,
  message,
  Row,
  Space,
} from "antd";

import { PrinterOutlined, TableOutlined } from "@ant-design/icons";
import axios from "axios";
import moment from "moment";
import { useCallback } from "react";
import { useSelector } from "react-redux";
import ReactToPrint from "react-to-print";
import RangeDatePickerFieldGroup from "../../commons/RangeDatePickerFieldGroup";
import SelectFieldGroup from "../../commons/SelectFieldGroup";
import numberFormat from "../../utils/numberFormat";
import { onSupplierSearch } from "../../utils/utilities";
import { smallFormItemLayout } from "./../../utils/Layouts";

const { Content } = Layout;

const url = "/api/stocks-receiving/";
const title = "Warehouse Receipt Report";

const initialValues = {
  period_covered: [moment(), moment()],
};
export default function StocksReceivingHistory() {
  const [errors, setErrors] = useState({});
  const [records, setRecords] = useState([]);
  const auth = useSelector((state) => state.auth);
  const [state, setState] = useState(initialValues);
  const [options, setOptions] = useState({
    customers: [],
  });
  const [loading, setLoading] = useState({
    search: false,
    download: false,
  });

  const report = useRef(null);

  const getReport = useCallback(
    ({ period_covered, branch, account, is_excel = false }) => {
      if (period_covered) {
        const form_data = {
          period_covered,
          branch,
          account,
          is_excel,
        };

        setLoading((prevState) => ({
          ...prevState,
          search: !is_excel,
          download: is_excel,
        }));

        axios
          .post(`${url}history`, form_data)
          .then((response) => {
            if (response.data) {
              if (is_excel) {
                setLoading((prevState) => ({
                  ...prevState,
                  download: false,
                }));
                return window.open(`/public/reports/${response.data.filename}`);
              }

              setRecords(response.data);
              setState((prevState) => ({
                ...prevState,
                total: response.data.total,
              }));
              setLoading((prevState) => ({
                ...prevState,
                search: false,
              }));
            }
          })
          .catch((err) => {
            message.error("There was an error processing your request");
          })
          .finally(() => {
            setLoading((prevState) => ({
              ...prevState,
              search: false,
              download: false,
            }));
          });
      } else {
        message.error("Please select period covered");
      }
    },
    []
  );

  return (
    <Content className="content-padding">
      <div className="columns is-marginless">
        <div className="column">
          <Breadcrumb style={{ margin: "16px 0" }}>
            <Breadcrumb.Item>Reports</Breadcrumb.Item>
            <Breadcrumb.Item>{title}</Breadcrumb.Item>
          </Breadcrumb>
        </div>
      </div>

      <div style={{ background: "#fff", padding: 24, minHeight: 280 }}>
        <span className="module-title">{title}</span>
        <Divider />
        <Row>
          <Col span={12}>
            <RangeDatePickerFieldGroup
              label="Period Covered"
              name="period_covered"
              value={state.period_covered}
              onChange={(dates) =>
                setState((prevState) => ({
                  ...prevState,
                  period_covered: dates,
                }))
              }
              error={errors.period_covered}
              formItemLayout={smallFormItemLayout}
            />
          </Col>
          <Col span={12}>
            <SelectFieldGroup
              label="Account"
              value={state.account?.name}
              onFocus={() => {
                onSupplierSearch({ value: "", setOptions });
              }}
              onSearch={(value) => onSupplierSearch({ value, setOptions })}
              onChange={(index) => {
                const account = options.accounts?.[index] || null;
                setState((prevState) => ({
                  ...prevState,
                  account,
                }));
              }}
              formItemLayout={smallFormItemLayout}
              data={options.accounts}
              column="name"
              error={errors.account}
              onAddItem={() => accountFormModal.current.open()}
            />
          </Col>
        </Row>

        <Row>
          <Col offset={4} span={12}>
            <Form.Item className="field is-grouped">
              <Space>
                <Button
                  onClick={() => {
                    getReport({
                      ...state,
                    });
                  }}
                  loading={loading.search}
                  disabled={loading.search}
                >
                  Search
                </Button>
                <Button
                  icon={<TableOutlined />}
                  onClick={() => {
                    getReport({
                      ...state,
                      is_excel: true,
                    });
                  }}
                  loading={loading.download}
                  disabled={loading.download}
                >
                  Download to Excel
                </Button>
                <ReactToPrint
                  trigger={() => (
                    <Button type="primary" icon={<PrinterOutlined />}>
                      Print
                    </Button>
                  )}
                  bodyClass="print"
                  content={() => report.current}
                />
              </Space>
            </Form.Item>
          </Col>
        </Row>
        <div ref={report}>
          <div>
            <div className="has-text-weight-bold">{title}</div>
            <div>
              Period Covered: {state.period_covered?.[0]?.format("MM/DD/YYYY")}{" "}
              to {state.period_covered?.[1]?.format("MM/DD/YYYY")}
            </div>
            <div>
              {state.branch?.company?.name || ""} - {state.branch?.name || ""}
            </div>
          </div>
          <table className="sales-report-table m-t-1">
            <thead>
              <tr>
                <th width={100}>Date</th>

                <th width={100}>RR#</th>
                <th width={100}>Ref</th>
                <th width={300}>Item</th>
                <th className="has-text-right" width={100}>
                  Qty
                </th>
                <th width={100} className="has-text-centered">
                  UOM
                </th>
                <th width={100} className="has-text-centered">
                  Price
                </th>
                <th width={100} className="has-text-centered">
                  Amount
                </th>
              </tr>
            </thead>
            <tbody>
              {records.map((record) => {
                return [
                  <tr className="tr-head" key="head">
                    <td rowSpan={record.items.length + 1}>
                      <div>{moment(record.date).format("MM/DD/YY")}</div>
                    </td>

                    <td>{record.rr_no}</td>
                    <td>{record.reference}</td>
                    <td>{record.account?.name}</td>
                    <td />
                    <td />
                    <td />
                    <td />
                  </tr>,
                  record.items.map((item) => {
                    return (
                      <tr>
                        <td></td>

                        <td>{item.stock?.name}</td>
                        <td></td>
                        <td className="has-text-right">
                          {numberFormat(item.quantity)}
                        </td>
                        <td className="has-text-centered">
                          {item.stock?.unit_of_measure}
                        </td>
                        <td className="has-text-right">
                          {numberFormat(item.price)}
                        </td>
                        <td className="has-text-right">
                          {numberFormat(item.amount)}
                        </td>
                      </tr>
                    );
                  }),
                  <tr className="tr-footer" key="footer">
                    <td></td>
                    <td></td>
                    <td></td>
                    <td></td>

                    <td className="has-text-right"></td>
                    <td />
                    <td className="has-text-right"></td>
                    <td className="has-text-right"></td>
                  </tr>,
                ];
              })}
              {/* <tr className="footer">
                <th className="has-text-right" colSpan={3}>
                  GRAND TOTAL
                </th>
                <th className="has-text-right">
                  {numberFormat(
                    sumBy(records, (record) =>
                      parseFloat(sumBy(record.items, (o) => o.quantity))
                    )
                  )}
                </th>
              </tr> */}
            </tbody>
          </table>
        </div>
      </div>
    </Content>
  );
}
